import {
  ArrowUUpLeftIcon,
  DownloadSimpleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PauseIcon,
  ProhibitIcon,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'

import { useCheckInVendor } from '@/api/visitors/check-in-vendor'
import { useExportVendors } from '@/api/visitors/export-vendors'
import { useGetVendors } from '@/api/visitors/get-vendors'
import { useUpdateVisitorStatus } from '@/api/visitors/update-visitor-status'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { StatusTabs } from '@/components/ui/status-tabs'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import LegacyVendors from '@/components/Visitors/Vendors/Vendors'
import { popupFactory, toastFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { VendorModel, VisitorStatus } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { buildCsvFilename, getApiErrorMessage } from '@/utils'

import { BanVisitorModal } from '../ban-visitor-modal'
import { VendorDetailsModal } from '../vendor-details-modal'
import { VisitorsDashboardOutletContext } from '../visitors-dashboard.page.types'
import { getVisitorStatusTabs, useDefaultVisitorsSort } from '../visitors-dashboard.utils'
import {
  getVendorColumns,
  vendorsInitialFilters,
  vendorsQueryFiltersSchema,
} from './visitors-vendors.constants'

export const VisitorsVendorsPage = () => {
  const { t } = useTranslation(['visitors'])
  const { uiVersion } = useOutletContext<VisitorsDashboardOutletContext>()
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id
  const timezone = activeBuilding?.timezone ?? 'America/Chicago'

  const searchParams = useSearchParams({
    initialState: vendorsInitialFilters,
    schema: vendorsQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: vendorsInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  useDefaultVisitorsSort({ filters, searchParams })

  const status = filters.getFilter('status')
  const search = filters.getFilter('search')
  const sortBy = filters.getFilter('sortBy')
  const sortOrder = filters.getFilter('sortOrder')
  const statusFilter = status === 'all' ? undefined : status

  const {
    data,
    isFetching: isVendorsFetching,
    error: vendorsError,
  } = useGetVendors({
    buildingId,
    direction: sortOrder,
    filter: statusFilter,
    limit: filters.getFilter('limit'),
    search: search || undefined,
    skip: filters.getFilter('skip'),
    sort: sortBy,
  })

  const { data: vendors = [], total = 0, countByStatus } = data ?? {}

  const { mutateAsync: checkInVendor } = useCheckInVendor()
  const { mutateAsync: updateStatus } = useUpdateVisitorStatus()
  const { mutateAsync: downloadVendors, isPending: isExporting } = useExportVendors()

  const handleExportCSV = () => {
    if (!buildingId) return
    void downloadVendors({
      buildingId,
      direction: sortOrder,
      fileName: buildCsvFilename({ building: activeBuilding?.name, page: 'Vendors' }),
      filter: statusFilter,
      search: search || undefined,
      sort: sortBy,
    })
  }

  const typeLabel = t('visitors:snackbar.typeVendor')

  const handleCheckIn = (vendor: VendorModel) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('visitors:dashboard.confirm.checkInBody', { name: vendor.name }),
      confirmText: t('visitors:columns.checkIn'),
      onConfirm: async () => {
        try {
          await checkInVendor({ vendorId: vendor.id })
          toastFactory.success(t('visitors:dashboard.snackbar.checkedIn', { name: vendor.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorCheckingIn', { type: typeLabel }),
          )
          throw error
        }
      },
      title: t('visitors:dashboard.confirm.checkInTitle', { type: typeLabel }),
      variant: 'info',
    })
  }

  const handleBan = (vendor: VendorModel) => {
    popupFactory.open(BanVisitorModal, {
      name: vendor.name,
      onConfirm: async (reason: string) => {
        try {
          await updateStatus({
            banned: true,
            bannedReason: reason,
            id: vendor.id,
            type: 'vendor',
          })
          toastFactory.success(t('visitors:dashboard.snackbar.banned', { name: vendor.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorBanning', { type: typeLabel }),
          )
          throw error
        }
      },
      type: 'vendor',
    })
  }

  const handleUnban = (vendor: VendorModel) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('visitors:dashboard.confirm.unbanBody', { name: vendor.name }),
      confirmText: t('visitors:columns.unban'),
      onConfirm: async () => {
        try {
          await updateStatus({
            banned: false,
            bannedReason: '',
            id: vendor.id,
            type: 'vendor',
          })
          toastFactory.success(t('visitors:dashboard.snackbar.unbanned', { name: vendor.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorUnbanning', { type: typeLabel }),
          )
          throw error
        }
      },
      title: t('visitors:dashboard.confirm.unbanTitle', { type: typeLabel }),
      variant: 'warning',
    })
  }

  const handleSuspend = (vendor: VendorModel) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('visitors:dashboard.confirm.suspendBody', { name: vendor.name }),
      confirmText: t('visitors:columns.suspend'),
      onConfirm: async () => {
        try {
          await updateStatus({
            id: vendor.id,
            suspended: true,
            type: 'vendor',
          })
          toastFactory.success(t('visitors:dashboard.snackbar.suspended', { name: vendor.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorSuspending', { type: typeLabel }),
          )
          throw error
        }
      },
      title: t('visitors:dashboard.confirm.suspendTitle', { type: typeLabel }),
      variant: 'warning',
    })
  }

  const handleUnsuspend = (vendor: VendorModel) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('visitors:dashboard.confirm.unsuspendBody', { name: vendor.name }),
      confirmText: t('visitors:columns.unsuspend'),
      onConfirm: async () => {
        try {
          await updateStatus({
            id: vendor.id,
            suspended: false,
            type: 'vendor',
          })
          toastFactory.success(t('visitors:dashboard.snackbar.unsuspended', { name: vendor.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorUnsuspending', { type: typeLabel }),
          )
          throw error
        }
      },
      title: t('visitors:dashboard.confirm.unsuspendTitle', { type: typeLabel }),
      variant: 'info',
    })
  }

  const columns = getVendorColumns({
    customAccessName: activeBuilding?.customAccessName,
    onCheckIn: handleCheckIn,
    t,
    timezone,
  })

  const rowActions: RowAction<VendorModel>[] = [
    {
      icon: EyeIcon,
      label: t('visitors:columns.details'),
      onClick: (vendor) => popupFactory.open(VendorDetailsModal, { vendorId: vendor.id }),
      primary: true,
    },
    {
      hidden: (vendor) => vendor.banned,
      icon: ProhibitIcon,
      label: t('visitors:columns.ban'),
      onClick: handleBan,
      variant: 'danger',
    },
    {
      hidden: (vendor) => !vendor.banned,
      icon: ArrowUUpLeftIcon,
      label: t('visitors:columns.unban'),
      onClick: handleUnban,
    },
    {
      hidden: (vendor) => vendor.suspended,
      icon: PauseIcon,
      label: t('visitors:columns.suspend'),
      onClick: handleSuspend,
    },
    {
      hidden: (vendor) => !vendor.suspended,
      icon: ArrowUUpLeftIcon,
      label: t('visitors:columns.unsuspend'),
      onClick: handleUnsuspend,
    },
  ]

  const statusTabOptions = getVisitorStatusTabs(t, countByStatus)

  const handleStatusChange = (newStatus: VisitorStatus | 'all') => {
    filters.setFilter('status', newStatus)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleSort = (newSortBy: string | null, newSortOrder: SortOrder | null) => {
    filters.setFilters((prevFilters) => ({
      ...prevFilters,
      sortBy: newSortBy ?? undefined,
      sortOrder: newSortOrder ?? undefined,
    }))
  }

  return uiVersion === 'legacy' ? (
    <LegacyVendors />
  ) : (
    <>
      <TableGroup
        id="visitors-vendors"
        columns={columns}
        data={vendors}
        error={vendorsError}
        getRowKey={(item) => item.id}
        loading={isVendorsFetching}
        mode="regular"
        rowActions={rowActions}
        tableFilters={filters}
        emptyNoFilters={{
          description: t('visitors:dashboard.vendors.emptyDescription'),
          title: t('visitors:dashboard.vendors.emptyTitle'),
        }}
        navigation={{
          actions: (
            <Button
              onlyBox
              disabled={isExporting}
              size="small"
              title={t('visitors:dashboard.actions.exportCsv')}
              use="secondary"
              onClick={handleExportCSV}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              placeholder={t('visitors:dashboard.filters.vendorSearchPlaceholder')}
              size="small"
              value={search}
              onChange={handleSearchChange}
            />
          ),
          statusTabs: (
            <StatusTabs options={statusTabOptions} value={status} onChange={handleStatusChange} />
          ),
        }}
        pagination={{
          paginationFilters: filters,
          total,
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
      />
    </>
  )
}
